import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { ROLES, ROLE_BADGE } from '../../lib/routeConfig';

/**
 * Invite / edit user modal (Phase 7).
 *
 * Props:
 *  - open, onClose
 *  - mode: 'create' | 'edit'
 *  - user (only in edit mode)
 *  - callCenters: [{id, name, commercial_name}]
 *  - onSubmit(form): async — parent handles the API call + toast
 *  - selfId: current session user's id (used to disable self-editing traps)
 *
 * The email field is:
 *  - required + editable in create mode
 *  - shown grayed-out in edit mode (never patchable server-side).
 *
 * The Call Center dropdown is only rendered when role === 'cc_agent'.
 */
const ROLE_OPTIONS = [
    { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
    { value: ROLES.MANAGER, label: 'Manager' },
    { value: ROLES.CC_AGENT, label: 'Agent CC' },
];

function isValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || '');
}

export default function UserFormDialog({
    open,
    onClose,
    mode = 'create',
    user = null,
    callCenters = [],
    onSubmit,
    selfId,
}) {
    const isEdit = mode === 'edit';
    const isSelfEditingSuperAdmin = isEdit && user?.id === selfId && user?.role === ROLES.SUPER_ADMIN;
    const [form, setForm] = React.useState({
        email: '',
        display_name: '',
        role: ROLES.CC_AGENT,
        call_center_id: '',
        is_active: true,
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setSubmitting(false);
        if (isEdit && user) {
            setForm({
                email: user.email || '',
                display_name: user.display_name || '',
                role: user.role || ROLES.CC_AGENT,
                call_center_id: user.call_center_id || '',
                is_active: !!user.is_active,
            });
        } else {
            setForm({ email: '', display_name: '', role: ROLES.CC_AGENT, call_center_id: '', is_active: true });
        }
    }, [open, isEdit, user]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        setError(null);
        // Validation
        if (!isEdit) {
            if (!isValidEmail(form.email)) { setError('Adresse Gmail invalide.'); return; }
        }
        if (!form.display_name.trim()) { setError("Nom d'affichage requis."); return; }
        if (!ROLE_OPTIONS.find((o) => o.value === form.role)) { setError('Rôle invalide.'); return; }
        if (form.role === ROLES.CC_AGENT && !form.call_center_id) {
            setError('Un Agent CC doit être rattaché à un call center.'); return;
        }
        setSubmitting(true);
        try {
            const payload = isEdit
                ? {
                    display_name: form.display_name.trim(),
                    role: form.role,
                    call_center_id: form.role === ROLES.CC_AGENT ? form.call_center_id : null,
                    is_active: form.is_active,
                }
                : {
                    email: form.email.trim(),
                    display_name: form.display_name.trim(),
                    role: form.role,
                    call_center_id: form.role === ROLES.CC_AGENT ? form.call_center_id : null,
                };
            await onSubmit(payload);
        } catch (e) {
            setError(e.message || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-white sm:max-w-[520px]" data-testid={isEdit ? 'user-edit-dialog' : 'user-invite-dialog'}>
                <DialogHeader>
                    <DialogTitle className="text-[#1A1D2E]">
                        {isEdit ? 'Modifier un utilisateur' : 'Inviter un utilisateur'}
                    </DialogTitle>
                    <DialogDescription className="text-[#6B7280]">
                        {isEdit
                            ? "Ajustez le rôle, le call center et le statut de l'utilisateur."
                            : "Ajoutez un nouvel utilisateur à la whitelist et envoyez-lui une invitation."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <FormRow label="Adresse Gmail">
                        <input
                            data-testid="user-form-email"
                            type="email"
                            value={form.email}
                            disabled={isEdit}
                            onChange={(e) => setField('email', e.target.value)}
                            placeholder="prenom.nom@gmail.com"
                            className={`h-10 px-3 rounded-lg border text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25 ${isEdit ? 'bg-[#F5F6FA] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed' : 'bg-white text-[#1A1D2E] border-[#E5E7EB]'}`}
                        />
                    </FormRow>

                    <FormRow label="Nom d'affichage">
                        <input
                            data-testid="user-form-display-name"
                            type="text"
                            value={form.display_name}
                            onChange={(e) => setField('display_name', e.target.value)}
                            placeholder="Ex: Sarah El Amrani"
                            className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                        />
                    </FormRow>

                    <FormRow label="Rôle">
                        <select
                            data-testid="user-form-role"
                            value={form.role}
                            disabled={isSelfEditingSuperAdmin}
                            onChange={(e) => setField('role', e.target.value)}
                            className={`h-10 px-3 rounded-lg border border-[#E5E7EB] text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25 ${isSelfEditingSuperAdmin ? 'bg-[#F5F6FA] text-[#6B7280] cursor-not-allowed' : 'bg-white text-[#1A1D2E]'}`}
                        >
                            {ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {isSelfEditingSuperAdmin && (
                            <span className="text-[11px] text-[#6B7280]">Vous ne pouvez pas changer votre propre rôle.</span>
                        )}
                    </FormRow>

                    {form.role === ROLES.CC_AGENT && (
                        <FormRow label="Call Center assigné">
                            <select
                                data-testid="user-form-call-center"
                                value={form.call_center_id}
                                onChange={(e) => setField('call_center_id', e.target.value)}
                                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                            >
                                <option value="">— Sélectionner —</option>
                                {callCenters.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.commercial_name ? `${c.commercial_name} (${c.name})` : c.name}
                                        {!c.is_active ? ' — inactif' : ''}
                                    </option>
                                ))}
                            </select>
                        </FormRow>
                    )}

                    {isEdit && (
                        <FormRow label="Statut">
                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 text-sm text-[#1A1D2E]">
                                    <input
                                        type="radio"
                                        name="is_active"
                                        data-testid="user-form-status-active"
                                        checked={form.is_active === true}
                                        onChange={() => setField('is_active', true)}
                                    />
                                    Actif
                                </label>
                                <label className="inline-flex items-center gap-2 text-sm text-[#1A1D2E]">
                                    <input
                                        type="radio"
                                        name="is_active"
                                        data-testid="user-form-status-inactive"
                                        checked={form.is_active === false}
                                        onChange={() => setField('is_active', false)}
                                    />
                                    Inactif
                                </label>
                            </div>
                        </FormRow>
                    )}

                    {!isEdit && (
                        <p className="text-[12px] text-[#6B7280] leading-relaxed pt-2 border-t border-[#E5E7EB]">
                            Un email d'invitation sera envoyé automatiquement à l'utilisateur pour qu'il puisse accéder à Nivo.
                        </p>
                    )}

                    {error && (
                        <div data-testid="user-form-error" className="text-sm text-[#EF4444]">{error}</div>
                    )}
                </div>

                <DialogFooter>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-10 px-4 rounded-lg border border-[#E5E7EB] bg-white text-[#1A1D2E] text-sm hover:bg-[#F5F6FA]"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        data-testid="user-form-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold disabled:opacity-40"
                    >
                        {submitting ? (isEdit ? 'Enregistrement…' : 'Envoi…') : (isEdit ? 'Enregistrer' : "Envoyer l'invitation")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FormRow({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[12px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
            {children}
        </label>
    );
}

export { ROLE_OPTIONS };
